import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";

const LOGO_SRC = "/assets/feeding-blue-logo.png";

const LINKS = [
  { href: "#about", label: "About" },
  { href: "#what-we-do", label: "What We Do" },
  { href: "#agencies", label: "For Agencies" },
  { href: "#vision", label: "Vision" },
  { href: "#events", label: "Events" },
  { href: "#story", label: "Story" },
  { href: "#leadership", label: "Leadership" },
  { href: "#join-team", label: "Hiring" },
  { href: "#contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (href) => {
    setOpen(false);
    if (!onHome) {
      navigate("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (!el) return;
    // First smooth scroll to trigger animations & bring section into view
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    // Correction pass after animations finalize (images load, reveals fire)
    // so anchor lands precisely under the fixed nav on any content length.
    setTimeout(() => {
      const target = document.querySelector(href);
      if (!target) return;
      const y = target.getBoundingClientRect().top + window.scrollY - 72;
      if (Math.abs(target.getBoundingClientRect().top - 0) > 8) {
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 700);
  };

  return (
    <header
      data-testid="site-nav"
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        scrolled || !onHome
          ? "bg-white/95 backdrop-blur-md border-b border-slate-200"
          : "bg-transparent border-b border-transparent"
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-20">
        <Link
          to="/"
          data-testid="nav-logo"
          className={cn(
            "flex items-center gap-3 font-display font-black tracking-tight text-base md:text-lg",
            scrolled || !onHome ? "text-slate-900" : "text-white"
          )}
        >
          <img
            src={LOGO_SRC}
            alt="Feeding Blue shield logo"
            className={cn(
              "h-9 md:h-10 w-auto object-contain transition-all",
              !(scrolled || !onHome) && "drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]"
            )}
          />
          <span className="hidden sm:inline">FEEDING BLUE</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-7">
          {LINKS.map((l) => {
            const isHiring = l.href === "#join-team";
            return (
              <button
                key={l.href}
                data-testid={`nav-link-${l.href.replace("#", "")}`}
                onClick={() => handleAnchor(l.href)}
                className={cn(
                  "text-sm font-semibold link-brut transition-colors relative",
                  isHiring
                    ? "text-blue-700 hover:text-blue-800"
                    : scrolled || !onHome
                      ? "text-slate-700 hover:text-black"
                      : "text-white/90 hover:text-white"
                )}
              >
                {l.label}
                {isHiring && (
                  <span
                    aria-hidden
                    className="absolute -top-2 -right-3 flex h-2 w-2"
                  >
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600" />
                  </span>
                )}
              </button>
            );
          })}
          <Link
            to="/donate"
            data-testid="nav-donate-cta"
            className="inline-flex items-center bg-blue-700 hover:bg-blue-800 text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors"
          >
            Donate
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className={cn(
            "lg:hidden p-2",
            scrolled || !onHome ? "text-slate-900" : "text-white"
          )}
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-panel" className="lg:hidden bg-white border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col gap-4">
            {LINKS.map((l) => {
              const isHiring = l.href === "#join-team";
              return (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.href.replace("#", "")}`}
                  onClick={() => handleAnchor(l.href)}
                  className={cn(
                    "text-left text-sm font-semibold py-2 border-b border-slate-100 flex items-center justify-between",
                    isHiring ? "text-blue-700" : "text-slate-800"
                  )}
                >
                  <span>{l.label}</span>
                  {isHiring && (
                    <span className="text-[10px] font-bold uppercase tracking-widest bg-blue-700 text-white px-2 py-1">
                      Now
                    </span>
                  )}
                </button>
              );
            })}
            <Link
              to="/donate"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-donate-cta"
              className="mt-2 inline-flex items-center justify-center bg-blue-700 text-white px-5 py-3 text-xs font-bold uppercase tracking-widest"
            >
              Donate
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
